import { DNAChipProps } from '@alucio/lux-ui'
import { DeviceMode } from 'src/state/context/AppSettings';

// Variant configs
const variantConfigs: Record<string, Partial<DNAChipProps>> = {
  MODIFIED: {
    testID: 'modified-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'MODIFIED',
  },
  UPDATED: {
    testID: 'updated-badge',
    appearance: 'outline',
    status: 'success',
    children: 'UPDATED',
  },
  USER_FILTER_MSL: {
    testID: 'user-filter-badge',
    appearance: 'filled',
    status: 'white',
  },
  USER_FILTER_PUBLISHER: {
    testID: 'user-filter-badge',
    appearance: 'subtle',
    status: 'secondary',
  },
  CONTENT_UNAVAILABLE: {
    testID: 'content-unavailable-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'CONTENT UNAVAILABLE',
  },
  REQUIRED: {
    testID: 'required-badge',
    appearance: 'outline',
    status: 'danger',
    children: 'REQUIRED',
  },
  SLIDE_COVER: {
    testID: 'slide-cover-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'COVER',
  },
  SLIDE_IMPORTED: {
    testID: 'slide-imported-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'IMPORTED',
  },
  SLIDE_CAN_INSERT_TEXT: {
    testID: 'slide-can-insert-text-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'CAN INSERT TEXT',
  },
  DOCUMENT_MY_UPLOADS: {
    testID: 'document-my-uploads-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'MY UPLOADS',
  },
  DOCUMENT_ASSOCIATED_FILE: {
    testID: 'document-associated-file-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'ASSOCIATED FILE',
  },
  MEETING_STATUS_PLANNED: {
    testID: 'meeting-status-planned-badge',
    appearance: 'outline',
    status: 'primary',
    children: 'PLANNED',
  },
  MEETING_STATUS_COMPLETED: {
    testID: 'meeting-status-completed-badge',
    appearance: 'outline',
    status: 'success',
    children: 'COMPLETED',
  },
  MEETING_STATUS_LOCKED: {
    testID: 'meeting-status-locked-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'LOCKED',
  },
  FOLDER_STATUS_UPDATED: {
    testID: 'folder-status-updated-badge',
    appearance: 'outline',
    status: 'success',
    children: 'UPDATED',
  },
  FOLDER_STATUS_NEEDS_REVIEW: {
    testID: 'folder-status-needs-review-badge',
    appearance: 'outline',
    status: 'warning',
    children: 'NEEDS REVIEW',
  },
  FOLDER_STATUS_SHARED: {
    testID: 'folder-status-shared-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'SHARED',
  },
  EMAIL_TEMPLATE_DRAFT: {
    testID: 'email-template-draft-badge',
    appearance: 'outline',
    status: 'secondary',
    children: 'DRAFT',
  },
  EMAIL_TEMPLATE_DEACTIVATED: {
    testID: 'email-template-deactivated-badge',
    appearance: 'outline',
    status: 'danger',
    children: 'DEACTIVATED',
  },
  CRM_STATUS_NOT_SYNCED: {
    testID: 'crm-status-not-synced-badge',
    appearance: 'outline',
    status: 'warning',
    children: 'NOT SYNCED',
  },
  CRM_STATUS_SAVED: {
    testID: 'crm-status-saved-badge',
    appearance: 'outline',
    status: 'primary',
    children: 'SAVED TO CRM',
  },
  CRM_STATUS_SUBMITTED: {
    testID: 'crm-status-submitted-badge',
    appearance: 'outline',
    status: 'success',
    children: 'SUBMITTED',
    iconLeft: 'lock-simple',
  },
};

export const DNAChipBeaconVariant = Object.fromEntries(
  Object.keys(variantConfigs).map(key => [key, key]),
) as { [K in keyof typeof variantConfigs]: K };

export type DNAChipBeaconVariant = keyof typeof variantConfigs;

// Main function to get variant props
export const getVariantProps = (
  variant?: DNAChipBeaconVariant,
  deviceMode: keyof typeof DeviceMode = 'desktop',
  children?: DNAChipProps['children'],
): Partial<DNAChipProps> => {
  const size = deviceMode === 'desktop' ? 'md' : 'lg';
  const overrideChildren = typeof children === 'string' ? children : undefined;

  if (!variant) return { size, children: overrideChildren };
  const config = variantConfigs[variant];
  if (!config) return { size, children: overrideChildren };

  const tabletCRMSavedStyle = deviceMode === 'tablet' && variant === 'CRM_STATUS_SAVED'
    ? { style: { minWidth: 115 } }
    : {};

  return {
    ...config,
    size,
    children: overrideChildren ?? config.children,
    ...tabletCRMSavedStyle,
  };
};
